import { Box } from "@otrium/core";
import { FavoriteBrandsWrapper, SearchWrapper } from "../Filter/Filter.styled";
import { InputTextSearch } from "../InputTextSearch";
import { Trans } from "@lingui/macro";
import {
  FilterContentWrapper,
  ScrollWrapper,
} from "src/organisms/ProductList/components/Filters/DesktopFilter.styled";
import { Button } from "src/atoms/Button";
import { LabelNavigation } from "../LabelNavigation";
import { MessageDescriptor } from "@lingui/core";
import classNames from "classnames";
import {
  MobileClearAllButton,
  MobileFilterContainer,
} from "./MobileFilter.styled";
import { ArrowLeft } from "src/organisms/MobileFilters/DrawerFilter.styled";
import { useWishListBrands } from "src/hooks/useWishListBrands";
import { useCallback, useMemo, useState } from "react";
import { Checkbox } from "src/atoms/Checkbox";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import { useListingState } from "src/hooks/stores/use-listing-store";
import type {
  ListingFacet,
  ListingFacetData,
} from "src/types/adapters/listing.type";

type MobileBrandFilterProps = {
  title: MessageDescriptor;
  showSearch: boolean;
  isOpen: boolean;
  facet: ListingFacet;
  toggleOpen: () => void;
  searchPlaceholder: string;
};

function compareByCount(a: ListingFacetData, b: ListingFacetData) {
  return b.count - a.count;
}

function MobileBrandFilter({
  title,
  showSearch,
  isOpen,
  facet,
  searchPlaceholder,
  toggleOpen,
}: MobileBrandFilterProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState("");
  const { refine, clearKey } = useFacetContext();
  const { totalResults: total } = useListingState();

  const facetData = useMemo(
    () => (facet.type !== "brand" ? [] : facet.data),
    [facet.type, facet.data]
  );

  const items = useMemo(() => {
    const filtered = searchQuery
      ? facetData.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : facetData;

    return [...filtered].sort(compareByCount);
  }, [facetData, searchQuery]);

  const selectedCount = useMemo(
    () => facetData.filter((option) => option.selected).length,
    [facetData]
  );

  const hasFilters = selectedCount > 0;

  const { data: favBrandsData } = useWishListBrands(9999, 0);

  const favoriteBrands = useMemo(() => {
    const favoriteBrandIds =
      favBrandsData?.wishlistBrands?.items.map((item) => item?.id) || [];

    return items
      .filter((item) => {
        const itemId = item.value.split("||")[0];

        return favoriteBrandIds?.includes(itemId);
      })
      .map((item) => item.value);
  }, [favBrandsData, items]);

  const favoriteBrandsSelected = useMemo(() => {
    if (favoriteBrands.length === 0) {
      return false;
    }

    const lookup = new Set(favoriteBrands);
    const favItems = items.filter((item) => lookup.has(item.value));

    return favItems.length > 0 && favItems.every((item) => item.selected);
  }, [favoriteBrands, items]);

  const handleRefine = useCallback(
    (value: string) => {
      refine(facet.key, value);
    },
    [facet.key, refine]
  );

  const toggleFavoriteBrands = useCallback(() => {
    favoriteBrands.forEach((value) => {
      if (!value) {
        return;
      }

      refine(facet.key, value);
    });
  }, [facet.key, favoriteBrands, refine]);

  const handleClear = useCallback(() => {
    clearKey(facet.key);
  }, [facet.key, clearKey]);

  return (
    <MobileFilterContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={title}
        leftAdornment={
          <MobileClearAllButton className="no-padding" onClick={toggleOpen}>
            <ArrowLeft strokeWidth="2" />
          </MobileClearAllButton>
        }
        rightAdornment={
          hasFilters ? (
            <MobileClearAllButton onClick={handleClear}>
              <Trans>Clear all</Trans>
            </MobileClearAllButton>
          ) : undefined
        }
      />
      <div className="content-wrapper">
        <SearchWrapper>
          {showSearch ? (
            <Box>
              <InputTextSearch
                placeholder={searchPlaceholder}
                handleChange={(event) =>
                  setSearchQuery(event.currentTarget.value)
                }
                onClearSearch={() => setSearchQuery("")}
              />
            </Box>
          ) : null}

          {favoriteBrands?.length ? (
            <FavoriteBrandsWrapper>
              <Checkbox
                name="Select my favourite brands"
                checked={favoriteBrandsSelected}
                onChange={toggleFavoriteBrands}
              >
                <span>
                  <Trans>Select my favourite brands</Trans>
                </span>
              </Checkbox>
            </FavoriteBrandsWrapper>
          ) : null}
        </SearchWrapper>

        <ScrollWrapper>
          <FilterContentWrapper
            as="ul"
            isMultiColumn={false}
            count={Math.round(items.length / 2)}
          >
            {items.map((item) => {
              return (
                <Box as="li" key={`${facet.key}-${item.id}`}>
                  <Checkbox
                    name={item.id}
                    checked={item.selected}
                    onChange={() => handleRefine(item.value)}
                  >
                    {item.label}
                  </Checkbox>
                </Box>
              );
            })}
          </FilterContentWrapper>
        </ScrollWrapper>

        <Button
          fullWidth
          variant="primary"
          onClick={toggleOpen}
          disabled={total <= 0}
          className="total-button"
        >
          <span>
            {total > 0 ? (
              <Trans>Show {total} results</Trans>
            ) : (
              <Trans>Close</Trans>
            )}
          </span>
        </Button>
      </div>
    </MobileFilterContainer>
  );
}

export { MobileBrandFilter };
